'use client';
import React, { Fragment } from 'react';
import { useState, useEffect } from 'react';
import { CounterInput } from '@shadcn/components/ui/input';
import { cn } from '@shadcn/lib/utils';
import { useTranslations, useLocale } from 'next-intl';
import Image from 'next/image';

import { Course, CoursePicture, Courseversion } from '@/payload/payload-types';
import Attention from '../../_icons/Attention';
import { useCart } from '../../_providers/Cart';
import { CartItem } from '../../_providers/Cart/reducer';
import { formatPrice } from '../../_utilities/format-price';
import { Flatten } from '../../_utilities/typescript';

import { Button } from '../Button';
import { useCourseVersion } from '../../_providers/CourseVersion';
import { CourseVersionSubTitle, CourseVersionTitle } from '../CourseVersionSelect';
import { ScrollArea } from '@shadcn/components/ui/scroll-area';
import { Chip } from '../Chip';
import { useToast } from '@shadcn/components/ui/use-toast';
import { Link } from '@/navigation';
import ChevronRight from '../../_icons/Chevron-right';
import Play from '../../_icons/Play';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@shadcn/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerTrigger,
	DrawerTitle,
	DrawerDescription,
} from '@shadcn/components/ui/drawer';
import dayjs from 'dayjs';
import { shouldShowCourseDiscountCardBadge } from '../../_utilities/should-show-course-card-badge';
import { CardBadge } from './CardBadge';
import ArrowDownLong from '../../_icons/Arrow-down-long';
import { useScroll } from '../../_providers/ScrollProvider';
import ChevronCircleTick from '../../_icons/Chevron-circle-tick';
import { getDiscountReduction, useTrackEvent } from '../../_providers/TrackEventProvider';
import { logToPayload } from '../../_utilities/log-to-payload';

const constructCartItem = (courseversion: Courseversion, quantity: number): CartItem => {
	return {
		orderedCollection: {
			value: courseversion,
			relationTo: 'courseversions',
		},
		quantity,
		id: courseversion.id,
		selfParticipant: false,
	};
};

export const CourseCard: React.FC<{
	course: Course;
	initialCourseversion: Flatten<Course['courseversions']>;
	className?: string;
	type?: 'mobile' | 'desktop';
}> = ({ course, initialCourseversion, className, type = 'desktop' }) => {
	const t = useTranslations('Course');
	const {
		hasInitializedCart,
		addItemToCart,
		cart,
		isItemInCart,
		deleteItemFromCart,
		isCartSyncInProgress,
	} = useCart();
	const locale = useLocale();
	const { selectedCourseVersion, setSelectedCourseVersion, allCourseVersions } =
		useCourseVersion();
	const { toast } = useToast();
	const { scrollToContactSection } = useScroll();
	const trackEvent = useTrackEvent();

	if (!selectedCourseVersion) {
		throw new Error(t('cta-subheadline'));
	}

	const [cartItem, setCartItem] = useState<CartItem>(
		constructCartItem(selectedCourseVersion, 0) as CartItem,
	);
	const [seatInputValue, setSeatInputValue] = React.useState<number>(
		(cartItem.quantity as number) > 0 ? (cartItem.quantity as number) : 1,
	);
	const courseversionLeadingLecturer = selectedCourseVersion.lecturers.find(
		_lecturer => _lecturer.isLeading,
	);
	const leadingLecturer = courseversionLeadingLecturer?.lecturer;

	const coursePicture = course.picture as CoursePicture;

	const totalSlots = selectedCourseVersion.totalSlots;
	const remainingSlots = selectedCourseVersion.remainingSlots || 0;
	const slotsInUse = totalSlots - remainingSlots;

	// business logic
	const remainingSlotsString =
		remainingSlots > 9
			? t('slots-remaining-alot')
			: t('slots-remaining', { slots: remainingSlots });

	useEffect(() => {
		const itemInCart =
			cart &&
			cart.cartItems &&
			cart?.cartItems.find(cartItem => cartItem.id === selectedCourseVersion.id);

		if (itemInCart) {
			setSeatInputValue(itemInCart?.quantity || 0);
			setCartItem(itemInCart);
		} else {
			setSeatInputValue(1);
			setCartItem(constructCartItem(selectedCourseVersion, 1));
		}
	}, [selectedCourseVersion, hasInitializedCart, cart.cartItems]);

	useEffect(() => {
		setCartItem({ ...cartItem, quantity: seatInputValue });
	}, [seatInputValue]);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const shouldShowDiscountedPrice =
		selectedCourseVersion.discountedPrice &&
		selectedCourseVersion.discountedPrice > 0 &&
		Boolean(selectedCourseVersion.discountName) &&
		Boolean(selectedCourseVersion.discountStatus === 'enabled');
	const hasCourseSalesRep = course.courseSalesRep && typeof course.courseSalesRep !== 'string';

	if (!course?.courseversions) {
		logToPayload('Course Card: course has no courseversions available');
		return null;
	}

	if (!selectedCourseVersion) {
		logToPayload('Course Card: no selected courseversion');
		return null;
	}

	if (!initialCourseversion || typeof initialCourseversion === 'string') {
		logToPayload('Course Card: no intial courseversion');
		return null;
	}

	const courseImage = coursePicture.sizes?.card?.url || coursePicture.url;

	if (!courseImage) {
		logToPayload('Card: CourseImage missing ', {
			coursePicture,
			courseversionId: selectedCourseVersion.id,
		});
	}

	if (typeof leadingLecturer === 'string' || !leadingLecturer) {
		logToPayload(
			`Bad lecturer for course: courseId ${course.id}. selectedCourseVersion: ${selectedCourseVersion.id}. leadingLecturer: ${leadingLecturer}`,
		);

		return null;
	}

	if (type === 'mobile') {
		return (
			<Drawer
				open={isDrawerOpen}
				onOpenChange={isOpen => {
					setIsDrawerOpen(isOpen);
				}}
			>
				<DrawerTrigger
					asChild
					className="bg-muted-navy fixed bottom-0 z-10 flex h-fit w-screen items-center justify-between rounded-t-3xl p-4 lg:hidden"
				>
					<div className="flex items-center justify-between space-x-4">
						<Chip
							size="large"
							bgColor={shouldShowDiscountedPrice ? 'green' : 'default'}
						>
							{isItemInCart(cartItem)
								? cartItem.quantity
								: formatPrice(
										selectedCourseVersion.discountedPrice ||
											selectedCourseVersion.price,
										locale,
										'EUR',
									)}
						</Chip>
						<Button
							variant="solid"
							intent="secondary"
							size="small"
							className="flex-1"
							loading={isCartSyncInProgress}
						>
							{isItemInCart(cartItem) ? t('update-cart') : t('add-to-cart')}
						</Button>
					</div>
				</DrawerTrigger>
				<VisuallyHidden.Root>
					<DrawerTitle>Shopping cart</DrawerTitle>
				</VisuallyHidden.Root>
				<VisuallyHidden.Root>
					<DrawerDescription>Shopping cart for adding course to cart</DrawerDescription>
				</VisuallyHidden.Root>
				<DrawerContent
					asChild
					className="bg-muted-navy h-fit w-screen max-w-screen min-w-screen rounded-t-3xl"
				>
					<div>
						<DrawerClose className="flex w-full justify-end" asChild>
							<Button intent="tertiary" variant="ghost" className="px-4 py-6">
								{t('close')}
							</Button>
						</DrawerClose>

						<div className="flex flex-col space-y-6 px-4 pb-6">
							<div className="flex flex-col space-y-2">
								<p className="text-gold-light text-xl leading-7 font-bold">
									{course.title}
								</p>
								{allCourseVersions.length === 1 && (
									<CourseVersionTitle
										className="text-white"
										courseVersion={allCourseVersions[0]}
									/>
								)}
							</div>

							{shouldShowDiscountedPrice ? (
								<div className="flex items-center">
									<Chip className="mr-4 w-fit" bgColor="green">
										{formatPrice(
											selectedCourseVersion.discountedPrice!,
											locale,
											'EUR',
										)}
									</Chip>
									<p className="text-muted-blue text-sm">
										{t('discount-until', {
											discountName: selectedCourseVersion.discountName,
											discountDeadline: dayjs(
												selectedCourseVersion.discountDeadline,
											).format('DD.MM.YYYY'),
										})}
										<br />
										{t('normal-price')}{' '}
										{formatPrice(selectedCourseVersion.price, locale, 'EUR')}
									</p>
								</div>
							) : (
								<Chip className="w-fit">
									{formatPrice(selectedCourseVersion.price, locale, 'EUR')}
								</Chip>
							)}

							{allCourseVersions.length > 1 && (
								<ScrollArea className="mb-5 flex max-h-48 flex-col space-y-2 rounded-2xl bg-neutral-50/5 p-1">
									{allCourseVersions.map((courseVersion: Courseversion) => (
										<a
											className={`flex cursor-pointer flex-col rounded-xl p-3 text-white ${courseVersion.id === selectedCourseVersion.id ? 'bg-gold-light' : ''}`}
											onClick={e => {
												e.preventDefault();
												setSelectedCourseVersion(courseVersion);
											}}
											key={courseVersion.id}
										>
											<CourseVersionTitle courseVersion={courseVersion} />
											<CourseVersionSubTitle courseVersion={courseVersion} />
										</a>
									))}
								</ScrollArea>
							)}
							<div className="flex flex-col space-y-3">
								<div className="flex items-center justify-start space-x-4">
									<CounterInput
										isLoading={isCartSyncInProgress}
										setValue={setSeatInputValue}
										type="number"
										value={seatInputValue}
										maxCount={remainingSlots || 0}
										size="small"
										minimumValue={isItemInCart(cartItem) ? 0 : 1}
									/>
									<Button
										variant="solid"
										intent="secondary"
										size="small"
										loading={isCartSyncInProgress}
										className="max-w-60 flex-1"
										onClick={() => {
											if ((cartItem?.quantity || 0) < 1) {
												deleteItemFromCart(cartItem);
												setSeatInputValue(1);
											} else {
												addItemToCart(cartItem);

												trackEvent('add_to_cart_clicked', {
													course_name: course.title,
													component: 'side_card_mobile',
													course_discount_value_eur:
														getDiscountReduction(selectedCourseVersion),
												});
											}
											setIsDrawerOpen(false);
										}}
									>
										{isItemInCart(cartItem)
											? seatInputValue >= 1
												? t('update')
												: t('remove')
											: t('add-to-cart')}
									</Button>
								</div>
								<p className="text-sm text-white italic">{remainingSlotsString}</p>
							</div>
							{hasCourseSalesRep && (
								<div className="bg-brand-navy flex items-center justify-center space-x-2 rounded-lg px-6 py-4 text-sm leading-6 text-white">
									<p className="font-bold">{t('not-sure')}</p>

									<Button
										className="gap-1 p-0"
										intent="tertiary"
										variant="ghost"
										onClick={e => {
											e.preventDefault();
											scrollToContactSection();
											setIsDrawerOpen(false);
										}}
									>
										{t('contact-us')} <ArrowDownLong />
									</Button>
								</div>
							)}
							<p className="text-muted-blue flex items-center text-sm italic">
								<span className="mr-4">
									<Attention color="#8EA0BB"></Attention>
								</span>
								{course.shouldAddVatToPrice
									? `${t('price-includes-vat')} (${formatPrice(selectedCourseVersion.discountedVAT || selectedCourseVersion.VAT || 0, locale, 'EUR')})`
									: t('price-does-not-include-vat')}
							</p>
						</div>
					</div>
				</DrawerContent>
			</Drawer>
		);
	}
	return (
		<div className={cn('bg-muted-navy hidden rounded-3xl lg:block', className)}>
			<div className="relative">
				{shouldShowCourseDiscountCardBadge({ courseversion: selectedCourseVersion }) && (
					<CardBadge
						title={selectedCourseVersion.discountName!}
						subTitle={`${t('until')} ${dayjs(selectedCourseVersion.discountDeadline).format('DD.MM.YYYY')}`}
						color="red"
					/>
				)}
				{courseImage && (
					<Image
						alt={coursePicture.alt}
						className="rounded-3xl"
						height={200}
						src={courseImage}
						width={400}
						style={{ objectFit: 'cover', height: '200px' }}
					/>
				)}

				{course.videoUrl && (
					<Dialog>
						<DialogTrigger
							onClick={() => {
								trackEvent('course_video_clicked', {
									course_name: course.title,
									component: 'desktop_side_card',
									course_discount_value_eur:
										getDiscountReduction(selectedCourseVersion),
								});
							}}
							className="group absolute inset-0 flex cursor-pointer items-center justify-center rounded-3xl bg-[#041E413D]/25"
						>
							<div className="flex h-full flex-col items-center justify-center space-y-4">
								<Play />

								<p className="text-sm leading-6 font-medium text-white group-hover:underline">
									{t('explore-course-content')}
								</p>
							</div>
						</DialogTrigger>
						<DialogContent className="h-4/5 max-h-screen w-4/5 max-w-screen">
							<DialogTitle>Video</DialogTitle>
							<iframe
								width="100%"
								height="100%"
								src={`${course.videoUrl}&autoplay=1`}
								title="YouTube video player"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								referrerPolicy="strict-origin-when-cross-origin"
								allowFullScreen
								style={{ borderRadius: 24 }}
							></iframe>
						</DialogContent>
					</Dialog>
				)}
			</div>

			<div className="rounded-3xl px-8 py-8">
				<div className="mb-6 flex flex-col">
					<p className="text-gold-light mr-2 mb-2 text-lg">{course.title}</p>
					{allCourseVersions.length === 1 && (
						<CourseVersionTitle
							className="mb-4 text-white"
							courseVersion={allCourseVersions[0]}
						/>
					)}

					<div className="flex items-center">
						{shouldShowDiscountedPrice ? (
							<Fragment>
								<p className="bg-success mr-2 rounded-3xl px-4 text-xs leading-8 font-bold text-white">
									{formatPrice(
										selectedCourseVersion.discountedPrice!,
										locale,
										'EUR',
									)}
								</p>
								<p className="text-muted-blue text-sm">
									{t('discount-until', {
										discountName: selectedCourseVersion.discountName,
										discountDeadline: dayjs(
											selectedCourseVersion.discountDeadline,
										).format('DD.MM.YYYY'),
									})}
									<br />
									{t('normal-price')}{' '}
									{formatPrice(selectedCourseVersion.price, locale, 'EUR')}
								</p>
							</Fragment>
						) : (
							<p className="bg-muted-blue rounded-3xl px-4 text-xs leading-8 font-bold text-white">
								{formatPrice(selectedCourseVersion.price, locale, 'EUR')}
							</p>
						)}
					</div>
				</div>

				{allCourseVersions.length > 1 && (
					<ScrollArea className="mb-5 flex max-h-48 flex-col space-y-2 rounded-2xl bg-neutral-50/5 p-1">
						{allCourseVersions.map((courseVersion: Courseversion) => (
							<a
								className={`flex cursor-pointer flex-col rounded-xl p-3 text-white ${courseVersion.id === selectedCourseVersion.id ? 'bg-gold-light' : ''}`}
								onClick={e => {
									e.preventDefault();
									setSelectedCourseVersion(courseVersion);
								}}
								key={courseVersion.id}
							>
								<CourseVersionTitle courseVersion={courseVersion} />
								<CourseVersionSubTitle courseVersion={courseVersion} />
							</a>
						))}
					</ScrollArea>
				)}

				<div className="mb-1 flex space-x-2">
					<CounterInput
						isLoading={isCartSyncInProgress}
						setValue={setSeatInputValue}
						type="number"
						value={seatInputValue}
						maxCount={remainingSlots || 0}
						size="large"
						minimumValue={0}
					/>
					<Button
						variant="solid"
						intent="secondary"
						className="flex-1 py-0"
						loading={isCartSyncInProgress}
						disabled={(selectedCourseVersion.remainingSlots || 0) < 1}
						onClick={() => {
							if ((cartItem?.quantity || 0) < 1) {
								deleteItemFromCart(cartItem);
								toast({
									title: t('removed-from-cart'),
								});
							} else {
								addItemToCart(cartItem);
								trackEvent('add_to_cart_clicked', {
									course_name: course.title,
									component: 'side_card',
									course_discount_value_eur:
										getDiscountReduction(selectedCourseVersion),
								});
								toast({
									title: t('added-to-cart'),
									action: (
										<Button
											className="p-0 text-white"
											variant="ghost"
											size="small"
										>
											<Link
												onClick={() => {
													trackEvent('go_to_cart_clicked', {
														component: 'toast',
													});
												}}
												href={'/cart'}
											>
												{t('go-to-cart')}
											</Link>
											<ChevronRight color="white" />
										</Button>
									),
								});
							}
						}}
						type={'button'}
					>
						{isItemInCart(cartItem) && seatInputValue >= 1
							? t('update-cart')
							: isItemInCart(cartItem) && seatInputValue < 1
								? t('remove-from-cart')
								: t('add-to-cart')}
					</Button>
				</div>
				<p className="mb-6 text-sm text-white italic">{remainingSlotsString}</p>
				<div className="flex flex-col space-y-6">
					{hasCourseSalesRep && (
						<div className="bg-brand-navy flex items-center justify-center space-x-2 rounded-lg px-6 py-4 leading-6 text-white">
							<p className="font-bold">{t('not-sure')}</p>

							<Button
								className="gap-1 p-0"
								intent="tertiary"
								variant="ghost"
								onClick={scrollToContactSection}
							>
								{t('contact-us')} <ArrowDownLong />
							</Button>
						</div>
					)}
					<p className="text-muted-blue flex items-center text-sm italic">
						<span className="mr-3">
							<Attention color="#8EA0BB"></Attention>
						</span>
						{course.shouldAddVatToPrice
							? `${t('price-includes-vat')} (${formatPrice(selectedCourseVersion.discountedVAT || selectedCourseVersion.VAT || 0, locale, 'EUR')})`
							: t('price-does-not-include-vat')}
					</p>
					{selectedCourseVersion.isInstalmentPaymentsEnabled && (
						<div className="flex items-center">
							<ChevronCircleTick className="bg-success rounded-lg" />
							<p className="text-success ml-3">
								{t('is-instalment-payments-enabled')}
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
